import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Icon, type IconName } from 'components/ui/icon';
import { X } from 'lucide-react';

const bannerVariants = cva(
  'relative w-full rounded-lg p-3 flex items-start gap-3',
  {
    variants: {
      variant: {
        success: '',
        warning: '',
        error: '',
        info: '',
      },
      appearance: {
        'left-border': 'bg-white border border-gray-200 border-l-4',
        filled: 'border',
      },
    },
    compoundVariants: [
      // left-border (default look)
      {
        variant: 'success',
        appearance: 'left-border',
        class: 'border-l-green-400',
      },
      {
        variant: 'warning',
        appearance: 'left-border',
        class: 'border-l-yellow-400',
      },
      {
        variant: 'error',
        appearance: 'left-border',
        class: 'border-l-red-400',
      },
      // `info` intentionally stays semantic BLUE (not brand green) so it reads
      // as "information", distinct from the success/brand green. It uses the
      // semantic blue-* tokens rather than the retired brand primary-* palette.
      {
        variant: 'info',
        appearance: 'left-border',
        class: 'border-l-blue-400',
      },
      // filled (tinted background)
      {
        variant: 'success',
        appearance: 'filled',
        class: 'bg-green-50 border-green-300',
      },
      {
        variant: 'warning',
        appearance: 'filled',
        class: 'bg-yellow-50 border-yellow-300',
      },
      {
        variant: 'error',
        appearance: 'filled',
        class: 'bg-red-50 border-red-300',
      },
      {
        variant: 'info',
        appearance: 'filled',
        class: 'bg-blue-50 border-blue-300',
      },
    ],
    defaultVariants: {
      variant: 'success',
      appearance: 'left-border',
    },
  }
);

type BannerVariant = 'success' | 'warning' | 'error' | 'info';
type BannerAppearance = 'left-border' | 'filled';

const iconMap: Record<BannerVariant, IconName> = {
  success: 'check-circle',
  warning: 'alert-circle',
  error: 'x-circle',
  info: 'info',
};

const iconColorMap: Record<BannerAppearance, Record<BannerVariant, string>> = {
  'left-border': {
    success: 'text-green-400',
    warning: 'text-yellow-400',
    error: 'text-red-400',
    info: 'text-blue-400',
  },
  filled: {
    success: 'text-green-600',
    warning: 'text-yellow-600',
    error: 'text-red-600',
    info: 'text-blue-600',
  },
};

export interface BannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bannerVariants> {
  message: React.ReactNode;
  details?: React.ReactNode;
  showClose?: boolean;
  onClose?: () => void;
}

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      className,
      variant = 'success',
      appearance = 'left-border',
      message,
      details,
      showClose = false,
      onClose,
      ...props
    },
    ref
  ) => {
    const resolvedVariant = (variant ?? 'success') as BannerVariant;
    const resolvedAppearance = (appearance ??
      'left-border') as BannerAppearance;
    const iconName = iconMap[resolvedVariant];
    const iconColor = iconColorMap[resolvedAppearance][resolvedVariant];

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(bannerVariants({ variant, appearance }), className)}
        {...props}
      >
        <Icon
          name={iconName}
          size={20}
          className={cn('shrink-0 mt-0.5', iconColor)}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'm-0 text-gray-blue-90',
              details
                ? 'text-base font-semibold leading-6'
                : 'text-sm font-medium'
            )}
          >
            {message}
          </p>
          {details && (
            <div className="mt-1 text-sm text-gray-blue-70">{details}</div>
          )}
        </div>
        {showClose && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-blue-60 hover:text-gray-blue-90 transition-colors"
            aria-label="Dismiss"
          >
            <X size={20} />
          </button>
        )}
      </div>
    );
  }
);
Banner.displayName = 'Banner';

export { Banner, bannerVariants };
